export interface TemplateVariables {
  count?: number | string;
}

/**
 * Replaces template variables in a string with their corresponding values.
 * Supports the following placeholders:
 * - `{{count}}` - Replaced with the asset count (formatted with locale)
 *
 * @param template - The string containing template placeholders
 * @param variables - Object containing values to replace placeholders with
 * @returns The string with placeholders replaced, or the original string if no replacements needed
 *
 * @example
 * replaceTemplateVariables("Browse {{count}} tracks", { count: 1234 })
 * // Returns: "Browse 1,234 tracks"
 */
export function replaceTemplateVariables(
  template: string | undefined,
  variables: TemplateVariables
): string | undefined {
  if (!template) {
    return template;
  }

  let result = template;

  if (variables.count === undefined) {
    // Remove unresolved {{count}} placeholders (and any trailing space)
    // to prevent literal template strings appearing in rendered output
    result = result.replace(/\{\{count\}\}\s*/g, '').trim();
  } else {
    const formattedCount =
      typeof variables.count === 'number'
        ? variables.count.toLocaleString('en-US')
        : variables.count;
    result = result.replaceAll('{{count}}', formattedCount);
  }

  return result || undefined;
}
